<template>
  <z-dialog
    v-if="initStore.columnMenu"
    v-model="columnMenuVisible"
    :title="dialogTitle"
    width="420px"
    align-center
    draggable
    destroy-on-close
    :class="ns.b()"
    @opened="focusValueSearchInput"
    @closed="handleHide"
  >
    <div v-if="column" :class="ns.e('body')">
      <div v-if="column.sortable" :class="ns.e('section')">
        <div :class="ns.e('section-label')">{{ t('z.table.columnMenu.sort') }}</div>
        <div :class="ns.e('actions')">
          <z-button
            size="small"
            :type="store.sortOrder === 'asc' ? 'primary' : 'default'"
            @click="store.sortOrder = 'asc'"
          >
            {{ t('z.table.columnMenu.sortAsc') }}
          </z-button>
          <z-button
            size="small"
            :type="store.sortOrder === 'desc' ? 'primary' : 'default'"
            @click="store.sortOrder = 'desc'"
          >
            {{ t('z.table.columnMenu.sortDesc') }}
          </z-button>
        </div>
      </div>

      <div :class="ns.e('section')">
        <div :class="ns.e('section-label')">{{ t('z.table.columnMenu.filter') }}</div>
        <div :class="ns.e('filter-row')" @keydown.enter.prevent="onFilterConfirm">
          <span :class="ns.e('filter-field')" :title="columnLabel">
            {{ columnLabel }}
          </span>
          <z-select
            v-model="store.filterOperator"
            size="small"
            :class="ns.e('filter-operator')"
          >
            <z-option
              v-for="opt in store.operatorList"
              :key="opt.value"
              :value="opt.value"
              :label="t(opt.label)"
            />
          </z-select>
          <grid-filter-value
            v-if="!isValuelessOperator(store.filterOperator) && column"
            v-model="store.filterValue"
            :column="column"
            size="small"
            :class="ns.e('filter-value')"
          />
          <div v-else :class="[ns.e('filter-value'), ns.e('filter-value-empty')]" />
        </div>
      </div>

      <div :class="ns.e('section')">
        <div :class="ns.e('section-label')">
          {{ t('z.table.columnMenu.valueList') }}
        </div>
        <div :class="ns.e('value-search-row')">
          <z-input
            ref="valueSearchInputRef"
            v-model="store.valueKeyword"
            size="small"
            clearable
            :class="ns.e('value-search-input')"
            :placeholder="t('z.table.columnMenu.valueListSearchPlaceholder')"
            @input="onValueKeywordInput"
            @clear="onValueKeywordClear"
          />
          <z-button
            size="small"
            :class="ns.e('value-search-btn')"
            @click="onValueKeywordSearch"
          >
            {{ t('z.table.columnMenu.valueListSearch') }}
          </z-button>
        </div>
        <div :class="ns.e('value-list-panel')">
          <div v-if="uniqueOptions.length" :class="ns.e('value-list')">
            <button
              v-for="opt in uniqueOptions"
              :key="String(opt.value)"
              type="button"
              :class="[
                ns.e('value-item'),
                ns.is('active', isSelectedListValue(opt.value)),
              ]"
              @click="selectValue(opt.value)"
              @mouseleave="onValueTextMouseLeave"
            >
              <span
                :class="ns.e('value-item-text')"
                @mouseenter="onValueTextMouseEnter($event, opt.label)"
              >
                {{ opt.label }}
              </span>
            </button>
          </div>
          <div v-else :class="ns.e('value-empty')">
            {{ t('z.table.emptyText') }}
          </div>
          <z-tooltip
            ref="valueTooltipRef"
            trigger="manual"
            placement="top"
            :teleported="true"
            :enterable="false"
            @hide="onValueTooltipHide"
          />
          <div
            v-if="store.valueOptionsLoading"
            :class="ns.e('value-loading')"
          >
            <span :class="ns.e('value-loading-spinner')" />
            <div :class="ns.e('value-loading-text')">
              {{ t('z.table.loadingText') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <z-button size="small" @click="$grid.restoreColumnMenu?.()">
        {{ t('z.table.columnMenu.restoreDefault') }}
      </z-button>
      <z-button type="primary" size="small" @click="$grid.applyColumnMenu?.()">
        {{ t('z.table.columnMenu.apply') }}
      </z-button>
    </template>
  </z-dialog>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { debounce } from 'lodash-es'
import ZButton from '@zoryn/components/button'
import ZDialog from '@zoryn/components/dialog'
import ZInput from '@zoryn/components/input'
import ZSelect, { ZOption } from '@zoryn/components/select'
import ZTooltip from '@zoryn/components/tooltip'
import { useLocale, useNamespace } from '@zoryn/hooks'
import GridFilterValue from './grid-filter-value.vue'
import { isValuelessOperator } from '../filter/filter-utils'
import { useOverflowTooltip } from '../hooks/use-overflow-tooltip'
import type { InputInstance } from '@zoryn/components/input'
import type { DataGrid } from '../types'

const ns = useNamespace('grid-column-menu')
const { t } = useLocale()
const $grid = inject('$grid') as DataGrid
const valueSearchInputRef = ref<InputInstance>()
const valueTooltipRef = ref<{
  open?: (target: HTMLElement, content: string) => void | Promise<unknown>
  hide?: () => void | Promise<unknown>
}>()
const hoveredValueLabel = ref('')
const clickedListValue = ref<unknown>(undefined)

const {
  onTextMouseEnter,
  onTextMouseLeave,
  onTooltipHide: onValueTooltipHide,
} = useOverflowTooltip(
  () => valueTooltipRef.value,
  () => hoveredValueLabel.value
)

const store = computed(() => $grid.reactData.columnMenuStore)
const initStore = computed(() => $grid.reactData.initStore)
const columnMenuVisible = computed({
  get: () => $grid.reactData.columnMenuStore.visible,
  set: (value: boolean) => {
    $grid.reactData.columnMenuStore.visible = value
  },
})

watch(columnMenuVisible, (visible) => {
  if (visible) {
    clickedListValue.value = undefined
  }
})

const column = computed(() => {
  const id = store.value.columnId
  if (!id) {
    return null
  }
  return $grid.internalData.allColumns.find((col) => col.id === id) ?? null
})

const columnLabel = computed(() => column.value?.title || column.value?.field || '')
const dialogTitle = computed(() =>
  columnLabel.value
    ? `${t('z.table.columnMenu.title')} - ${columnLabel.value}`
    : t('z.table.columnMenu.title')
)

const uniqueOptions = computed(() => {
  if (!column.value) {
    return []
  }
  return $grid.getColumnUniqueOptions?.(column.value) ?? []
})

function isSameFilterValue(left: unknown, right: unknown) {
  if (left === right) {
    return true
  }
  if (left == null || right == null) {
    return false
  }
  return String(left) === String(right)
}

function isSelectedListValue(value: unknown) {
  if (clickedListValue.value === undefined) {
    return false
  }
  return isSameFilterValue(clickedListValue.value, value)
}

function selectValue(value: unknown) {
  store.value.filterOperator = 'eq'
  store.value.filterValue = value
  clickedListValue.value = value
}

function onValueTextMouseEnter(event: MouseEvent, label: string) {
  hoveredValueLabel.value = label
  void onTextMouseEnter(event)
}

function onValueTextMouseLeave() {
  onTextMouseLeave()
}

function onFilterConfirm() {
  $grid.applyColumnMenu?.()
}

const debouncedKeywordSearch = debounce(() => {
  $grid.reloadColumnDistinctValues?.()
}, 300)

function onValueKeywordInput() {
  debouncedKeywordSearch()
}

function onValueKeywordSearch() {
  debouncedKeywordSearch.cancel()
  $grid.reloadColumnDistinctValues?.()
}

function onValueKeywordClear() {
  debouncedKeywordSearch.cancel()
  onValueKeywordSearch()
}

onBeforeUnmount(() => {
  debouncedKeywordSearch.cancel()
})

async function focusValueSearchInput() {
  await nextTick()
  valueSearchInputRef.value?.focus()
}

function handleHide() {
  $grid.closeColumnMenu?.()
}
</script>
